import * as React from "react";

import {
  makeRetinaSrcsets,
  getHeightFromWidthAndAspectRatio,
  makeFallbackImage,
  stripMarkup,
  formatImageCaption,
} from "./utils";
import styles from "./styles.module.css";
import { DEFAULT_NATIVE_WIDTH } from "~constants";
import { Image } from "~types/types";
import PlayIcon from "~icons/PlayIcon";

interface LazyImageProps {
  altFallback?: string;
  aspectRatio?: number;
  contentType?: string;
  height?: number;
  image: Image | undefined;
  isLazy?: boolean;
  width?: number;
  className?: string;
  desktopMaxWidth?: number;
  mobileMaxWidth?: number;
  showCaption?: boolean;
}

const DEFAULT_DESKTOP_WIDTH = DEFAULT_NATIVE_WIDTH;
const DEFAULT_MOBILE_WIDTH = DEFAULT_DESKTOP_WIDTH / 2;

export default function LazyRetinaImage({
  altFallback,
  aspectRatio,
  contentType,
  image,
  isLazy = true,
  className,
  desktopMaxWidth = DEFAULT_DESKTOP_WIDTH,
  mobileMaxWidth = DEFAULT_MOBILE_WIDTH,
  showCaption = false,
}: LazyImageProps) {
  const { baseUrl = "", alt, credit } = image || {};

  const altText = alt || altFallback;

  const styleObject = {
    "--aspect-ratio": aspectRatio && Number(aspectRatio?.toFixed(2)),
  } as React.CSSProperties;

  const { mobile: mobileSrcSet, desktop: desktopSrcSet } =
    makeRetinaSrcsets({
      baseUrl,
      desktopMaxWidth,
      mobileMaxWidth,
      aspectRatio,
    }) || {};

  const mobileMaxHeight = getHeightFromWidthAndAspectRatio(
    mobileMaxWidth,
    aspectRatio
  );

  const replacedCaption = formatImageCaption(credit || "");

  return (
    <figure data-component="lazy-image" className={className}>
      <picture className={styles.lazy} style={styleObject}>
        {contentType === "relatedWithPlayIcon" && <PlayIcon />}
        <source
          media="screen and (max-width: 759px)"
          srcSet={mobileSrcSet ?? ""}
        />
        <source
          media="screen and (min-width: 759px)"
          srcSet={desktopSrcSet ?? ""}
        />
        <img
          src={makeFallbackImage(baseUrl, mobileMaxWidth, mobileMaxHeight)}
          data-component="image"
          alt={altText}
          loading={isLazy ? "lazy" : "eager"}
        />
      </picture>
      {showCaption && Boolean(credit) && (
        <figcaption
          className={styles.caption}
          data-component="lazy-image-caption"
        >
          {stripMarkup(replacedCaption)}
        </figcaption>
      )}
    </figure>
  );
}
